import { Layer, project32 } from "@deck.gl/core"
import type { LayerContext, LayerProps } from "@deck.gl/core"
import type { Texture } from "@luma.gl/core"
import { Geometry, Model } from "@luma.gl/engine"
import { FrameImage, FrameTextureCache } from "../shared/frameTextureCache"
import { buildLatBandedStrip } from "../shared/heatmapGeometry"

const MASK_LAT_BANDS = 512
const MASK_QUAD_POSITIONS = buildLatBandedStrip(MASK_LAT_BANDS)
const MASK_QUAD_VERTEX_COUNT = 2 * (MASK_LAT_BANDS + 1)

const MASK_WORLD_BOUNDS: [number, number, number, number] = [-180, -85.051129, 180, 85.051129]

const MASK_VS = `#version 300 es
in vec2 positions;
out vec2 v_lonLat;

uniform vec4 u_worldBounds;

void main() {
    vec2 n = positions * 0.5 + 0.5;
    float lon = mix(u_worldBounds.x, u_worldBounds.z, n.x);
    float lat = mix(u_worldBounds.y, u_worldBounds.w, n.y);
    v_lonLat = vec2(lon, lat);
    gl_Position = project_position_to_clipspace(vec3(lon, lat, 0.0), vec3(0.0), vec3(0.0));
}
`

const MASK_BLUR_RADIUS = 4
const MASK_BLUR_SIGMA = 2.0
const MASK_FS = `#version 300 es
precision highp float;
precision highp sampler2D;

in vec2 v_lonLat;

uniform vec4 u_gridBounds; // west, south, east, north of the coverage texture
uniform sampler2D u_coverage;
uniform vec4 u_overlayColor;

const int BLUR_RADIUS = ${MASK_BLUR_RADIUS};
const float BLUR_SIGMA = ${MASK_BLUR_SIGMA.toFixed(1)};

out vec4 fragColor;

void main() {
    float u = (v_lonLat.x - u_gridBounds.x) / (u_gridBounds.z - u_gridBounds.x);
    float v = (v_lonLat.y - u_gridBounds.y) / (u_gridBounds.w - u_gridBounds.y);
    if (u < 0.0 || u > 1.0 || v < 0.0 || v > 1.0) {
        fragColor = u_overlayColor;
        return;
    }

    vec2 uv = vec2(u, 1.0 - v);
    vec2 texel = 1.0 / vec2(textureSize(u_coverage, 0));

    float coverage = 0.0;
    float weightSum = 0.0;
    for (int dy = -BLUR_RADIUS; dy <= BLUR_RADIUS; dy++) {
        for (int dx = -BLUR_RADIUS; dx <= BLUR_RADIUS; dx++) {
            vec2 offset = vec2(float(dx), float(dy));
            float weight = exp(-dot(offset, offset) / (2.0 * BLUR_SIGMA * BLUR_SIGMA));
            coverage += weight * texture(u_coverage, uv + texel * offset).a;
            weightSum += weight;
        }
    }
    coverage /= weightSum;

    float covered = smoothstep(0.35, 0.65, coverage);
    fragColor = u_overlayColor;
    fragColor.a *= 1.0 - covered;
}
`

export type RadarCoverageMaskLayerProps = LayerProps & {
    coverage: FrameImage
    bounds: [number, number, number, number]
    overlayColor: [number, number, number, number]
}

export class RadarCoverageMaskLayer extends Layer<RadarCoverageMaskLayerProps> {
    static layerName = "RadarCoverageMaskLayer"
    static defaultProps = {
        bounds: { type: "array", value: [-180, -90, 180, 90], compare: true },
        overlayColor: { type: "array", value: [20, 20, 20, 100], compare: true },
    }

    declare state: {
        textureCache: FrameTextureCache
        model: Model | null
    }

    getModels() {
        return this.state?.model ? [this.state.model] : []
    }

    initializeState(context: LayerContext): void {
        const { device } = context

        const shaders = this.getShaders({ vs: MASK_VS, fs: MASK_FS, modules: [project32] })
        const model = new Model(device, {
            id: "radar-coverage-mask",
            ...shaders,
            geometry: new Geometry({
                topology: "triangle-strip",
                attributes: {
                    positions: { size: 2, value: MASK_QUAD_POSITIONS },
                },
            }),
            vertexCount: MASK_QUAD_VERTEX_COUNT,
            parameters: {
                blendColorSrcFactor: "src-alpha",
                blendColorDstFactor: "one-minus-src-alpha",
                blendAlphaSrcFactor: "one",
                blendAlphaDstFactor: "one-minus-src-alpha",
            },
        })

        this.setState({ textureCache: new FrameTextureCache(), model })
    }

    finalizeState(context: LayerContext): void {
        this.state.textureCache?.destroy()
        this.state.model?.destroy()
        super.finalizeState(context)
    }

    draw(): void {
        const { model, textureCache } = this.state
        const { coverage, overlayColor } = this.props
        if (!model || !coverage || coverage.width === 0 || coverage.height === 0) return

        const create = (image: FrameImage): Texture =>
            this.context.device.createTexture({
                width: image.width,
                height: image.height,
                format: "rgba8unorm",
                data: image.bitmap,
                sampler: { magFilter: "linear", minFilter: "linear" },
            })

        model.setBindings({ u_coverage: textureCache.get(coverage, create) })
        model.setUniforms({
            u_worldBounds: MASK_WORLD_BOUNDS,
            u_gridBounds: this.props.bounds,
            u_overlayColor: overlayColor.map((c) => c / 255),
        })
        model.draw(this.context.renderPass)
    }
}
